import type { BaseAPI } from '~/api'
import { Configuration } from '~/api'

const setHeader = (requestInit: RequestInit, key: string, value: string) => {
  const newHeaders = new Headers(requestInit.headers)
  newHeaders.set(key, value)
  requestInit.headers = newHeaders
}

// 同時に複数のAPIが448を返した場合に、なりかわりトークンの交換を1回にまとめる
let substituteTokenRequest:
  | Promise<{ newAuthToken: string } | undefined>
  | undefined

/**
 * 設定されたAPIインスタンスを返します
 * @param apiClass API Class
 * @param authOperation 認証トークン失効時の再発行・自動ログアウトの有無(デフォルト: true)
 * @returns
 */
export const useApi = <TApiClass extends BaseAPI>(
  ApiClass: new (configuration: Configuration) => TApiClass,
  options: {
    noAuthToken?: boolean
    noTokenRefresh?: boolean
  } = {},
) => {
  const { apiBasePath, apiBaseUrl, useApiProxy, apiToken } =
    useRuntimeConfig().public
  const { authToken, accountInfo, signOutOnExpired, setAuthToken } = useAuth()
  const basePath = (() => {
    if (!import.meta.server && useApiProxy) {
      return apiBasePath
    } else {
      return apiBaseUrl + apiBasePath
    }
  })()
  const apiKey = (name: string) => {
    switch (name.toLowerCase()) {
      case 'x-api-token':
        return apiToken || ''
      case 'x-admin-token':
        return !options.noAuthToken ? authToken?.value || '' : ''
      default:
        return ''
    }
  }

  const reissueToken = async () => {
    if (!authToken.value) {
      return
    }
    const certificationApi = new CertificationApi(
      new Configuration({
        basePath,
        apiKey,
        middleware: [
          {
            post: async ({ response }) => {
              response.errorData = await response.clone().json()
              return response
            },
          },
        ],
      }),
    )

    try {
      const { adminToken: newAuthToken } = await certificationApi.reissue()
      return { newAuthToken }
    } catch {
      return undefined
    }
  }

  // 所有企業へなりかわった認証トークンに交換する
  const substituteToken = async (companyCode: string) => {
    const accountApi = new AccountApi(new Configuration({ basePath, apiKey }))

    try {
      const { adminToken: newAuthToken } = await accountApi.getTokenSubstitute({
        getTokenSubstituteRequest: { companyCode },
      })
      return { newAuthToken }
    } catch {
      return undefined
    }
  }

  return new ApiClass(
    new Configuration({
      basePath,
      apiKey,
      middleware: [
        {
          post: async ({ response, fetch, url, init }) => {
            response.errorData = await response.clone().json()
            if (options.noTokenRefresh) {
              return response
            }
            switch (response.errorData?.status) {
              case apiErrorStatuses.LOGIN_EXPIRED:
              case apiErrorStatuses.INVALID_AUTH_TOKEN: {
                signOutOnExpired()
                return response
              }
              case apiErrorStatuses.TOKEN_EXPIRED: {
                const result = await reissueToken()
                if (result) {
                  setAuthToken(result.newAuthToken)
                  setHeader(init, 'X-ADMIN-Token', result.newAuthToken)
                  return await fetch(url, init)
                } else {
                  signOutOnExpired()
                  return response
                }
              }
              case apiErrorStatuses.SUBSTITUTE_REQUIRED: {
                const companyCode: string | undefined =
                  response.errorData?.substitute_company_code
                // 既にその企業になりかわっている場合はリロードを繰り返さないよう通常のエラーとして扱う
                if (
                  !companyCode ||
                  companyCode === accountInfo.value?.companyCode
                ) {
                  return response
                }
                substituteTokenRequest ??= substituteToken(companyCode)
                const result = await substituteTokenRequest
                if (!result) {
                  substituteTokenRequest = undefined
                  return response
                }
                setAuthToken(result.newAuthToken)
                window.location.reload()
                // リロードまでの間に呼び出し元でエラー表示されないよう、応答を返さずに待たせる
                return new Promise<Response>(() => {})
              }
              default:
                return response
            }
          },
        },
      ],
    }),
  )
}
